import type { ReactNode } from 'react'
import { AlertOctagon, Inbox, RefreshCw, ShieldAlert, WifiOff } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { cn } from '@/lib/cn'
import { Button } from './Button'
import styles from './States.module.css'

interface EmptyStateProps {
  title: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
  className?: string
  compact?: boolean
}

export function EmptyState({ title, description, icon, action, className, compact = false }: EmptyStateProps) {
  return (
    <div className={cn(styles.state, compact && styles.compact, className)}>
      <span className={styles.icon}>{icon ?? <Inbox aria-hidden="true" />}</span>
      <p className={styles.title}>{title}</p>
      {description ? <p className={styles.description}>{description}</p> : null}
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  )
}

interface ErrorStateProps {
  error: unknown
  onRetry?: () => void
  className?: string
  compact?: boolean
  /** Overrides the derived heading. */
  title?: string
}

/**
 * Turns a failure into something an admin can act on.
 *
 * The message distinguishes the cases that need different responses — the
 * network is down, the session ended, the account lacks permission, the server
 * broke — instead of showing one generic apology for all of them.
 */
export function ErrorState({ error, onRetry, className, compact = false, title }: ErrorStateProps) {
  const { heading, message, icon } = describeError(error, title)

  return (
    <div className={cn(styles.state, styles.error, compact && styles.compact, className)} role="alert">
      <span className={cn(styles.icon, styles.errorIcon)}>{icon}</span>
      <p className={styles.title}>{heading}</p>
      <p className={styles.description}>{message}</p>
      {onRetry ? (
        <div className={styles.action}>
          <Button variant="secondary" onClick={onRetry} iconLeft={<RefreshCw aria-hidden="true" />}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function describeError(error: unknown, overrideTitle?: string): {
  heading: string
  message: string
  icon: ReactNode
} {
  if (error instanceof ApiError) {
    if (error.isNetwork) {
      return {
        heading: overrideTitle ?? 'Cannot reach the API',
        message: error.message,
        icon: <WifiOff aria-hidden="true" />,
      }
    }
    if (error.isForbidden) {
      return {
        heading: overrideTitle ?? 'Not permitted',
        message: error.message,
        icon: <ShieldAlert aria-hidden="true" />,
      }
    }
    return {
      heading: overrideTitle ?? (error.status >= 500 ? 'The server had a problem' : 'That did not work'),
      message: error.message,
      icon: <AlertOctagon aria-hidden="true" />,
    }
  }

  return {
    heading: overrideTitle ?? 'Something went wrong',
    message: error instanceof Error && error.message ? error.message : 'An unexpected error occurred.',
    icon: <AlertOctagon aria-hidden="true" />,
  }
}
